"use client";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  getMessageFromNetworkError,
  Template,
  useDuplicateTemplate,
} from "@zapier/client";

// design-contract: the repo overlay prefers @zapier/design-system-beta, but that
// package is not installed here — the beta components ship inside
// @zapier/design-system under a `Beta*` prefix. Followed the floor's
// "verify names before writing" rule and used the installed package.
import {
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  TextInput,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import {
  getTemplateDuplicateErrorType,
  TemplateDuplicateModalAction,
  useAutoSelectInputText,
  useEmitTemplateDuplicateModalEvent,
} from "@zapier/shared";
import { useNotification } from "@zapier/utils";

/** Matches the upstream templates collection `name` field, which is required with maxLength 256. */
const NAME_MAX_LENGTH = 256;

const NAME_INPUT_ID = "duplicate-template-name";

interface TemplateDuplicateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | undefined;
  /** Current user id, for the duplicate-modal interaction events. */
  customUserId?: number;
}

/**
 * Confirmation modal for duplicating a template, with an editable name for the copy.
 * Prefilled with the same "Copy of {name}" the templates backend would generate, so
 * submitting unchanged matches the backend default. On success shows a notification
 * and closes (the list updates via invalidation); on error shows a notification and
 * keeps the modal open, matching TemplateDeleteModal.
 */
export function TemplateDuplicateModal({
  isOpen,
  onClose,
  template,
  customUserId,
}: TemplateDuplicateModalProps) {
  const t = useTranslations("template-duplicate-modal");
  const { createNotification } = useNotification();
  const { emit: emitDuplicateModal } = useEmitTemplateDuplicateModalEvent();
  const { mutate: duplicateTemplate, isPending } = useDuplicateTemplate();

  const defaultName = template
    ? t("default-name", { templateName: template.name })
    : "";
  const [name, setName] = useState(defaultName);

  // The modal stays mounted between openings, so re-seed when it reopens for another row.
  useEffect(() => {
    if (!isOpen) return;
    setName(defaultName);
  }, [defaultName, isOpen]);

  /**
   * Set for the duration of a successful duplication so the close it triggers is
   * not also reported as a discard. Without this, every completed duplicate
   * would emit both `duplicated` and `discarded`, because onSuccess calls
   * onClose (CRTN-10662).
   */
  const hasDuplicatedRef = useRef(false);

  // Emit `viewed` once per opening rather than per render. Keyed on the template
  // id as well as isOpen because the modal stays mounted between openings, so
  // reopening it for a different row must re-fire.
  const viewedTemplateIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isOpen || !template) {
      viewedTemplateIdRef.current = undefined;
      return;
    }
    if (viewedTemplateIdRef.current === template.id) return;

    viewedTemplateIdRef.current = template.id;
    hasDuplicatedRef.current = false;
    void Promise.resolve(
      emitDuplicateModal(TemplateDuplicateModalAction.Viewed, {
        template_id: template.id,
        customuser_id: customUserId,
      }),
    ).catch((error: unknown) => {
      console.error("Failed to emit duplicate modal viewed event:", error);
    });
  }, [isOpen, template, customUserId, emitDuplicateModal]);

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${NAME_INPUT_ID}`,
    value: defaultName,
  });

  const trimmedName = name.trim();

  if (!isOpen || !template) {
    return;
  }

  const handleSubmit = () => {
    // Should not be reachable while the button is disabled, but the form can also
    // submit on Enter.
    if (!trimmedName || isPending) return;

    duplicateTemplate(
      { id: template.id, name: trimmedName },
      {
        onSuccess: (created) => {
          createNotification({
            message: t("notifications.duplicate-success"),
            type: "success",
          });
          hasDuplicatedRef.current = true;
          emitDuplicateModal(TemplateDuplicateModalAction.Duplicated, {
            template_id: template.id,
            customuser_id: customUserId,
            new_template_id: created.id,
            name_changed: trimmedName !== defaultName,
          });
          onClose();
        },
        onError: (error) => {
          createNotification({
            title: t("notifications.unable-to-duplicate"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
          emitDuplicateModal(TemplateDuplicateModalAction.Failed, {
            template_id: template.id,
            customuser_id: customUserId,
            error_type: getTemplateDuplicateErrorType(error.data?.httpStatus),
          });
        },
      },
    );
  };

  const isSubmitDisabled = !trimmedName || isPending;

  /**
   * Closes the modal, reporting an explicit cancel as a discard (CRTN-10662).
   *
   * Covers the footer Cancel, the X, Esc and an overlay click, since all of them
   * route through onClose. Emits nothing when the close follows a successful
   * duplication — that is a completion, not a discard — and nothing on a silent
   * navigation away or browser close, which never reaches this handler. Overall
   * abandonment is derived instead from a `viewed` with no terminal event.
   */
  const handleClose = () => {
    if (!hasDuplicatedRef.current && template) {
      emitDuplicateModal(TemplateDuplicateModalAction.Discarded, {
        template_id: template.id,
        customuser_id: customUserId,
      });
    }
    onClose();
  };

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <ModalDialogHeader textAlign="start">
          {t("header.title")}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <Field
            label={t("labels.name")}
            inputId={NAME_INPUT_ID}
            isRequired
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                autoFocus
                id={NAME_INPUT_ID}
                value={name}
                maxLength={NAME_MAX_LENGTH}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setName(event.target.value)
                }
                isFullWidth
              />
            )}
          />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            variant="secondary"
            type="button"
            onPress={handleClose}
            isDisabled={isPending}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            isDisabled={isSubmitDisabled}
            isPending={isPending}
          >
            {t("buttons.duplicate")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default TemplateDuplicateModal;
